import { useCallback, useRef } from 'react';
import {
  OTP_RECOVERY_RESEND_LABEL,
  OTP_RECOVERY_VERIFY_SUBTITLE,
  OTP_RECOVERY_VERIFY_TITLE,
  OTP_VERIFICATION_BACK_LABEL,
  OTP_VERIFICATION_NEXT_LABEL,
} from 'common/constants/auth';
import { ENTER_KEY } from 'common/constants/general';
import { Alert, Button, Stack } from 'components/ui';
import type { InputProps } from 'components/ui';
import AuthenticationRecoveryCodeInput from 'pages/Auth/components/AuthenticationRecoveryCodeInput';
import AuthExtraActions from 'pages/Auth/components/AuthExtraActions';
import AuthFormHeader from 'pages/Auth/components/AuthFormHeader';
import AuthPageStack from 'pages/Auth/components/AuthPageStack';
import AuthResendLabel from 'pages/Auth/components/AuthResendLabel';
import AuthStatusActionLink from 'pages/Auth/components/AuthStatusActionLink';
import AuthTitleText from 'pages/Auth/components/AuthTitleText';
import { ActionButtons, otpActionButtonSx, RootPaper, Subtitle, Title } from 'pages/Auth/components/OTP/OTP.styles';
import { OTP_TEST_IDS } from 'pages/Auth/components/OTP/OTP.testIds';
import { getFirstFieldMessage, isOtpSubmitDisabled } from 'pages/Auth/components/OTP/OTP.utils';
import { useAuthInitialFocus } from 'pages/Auth/hooks';
import type { OtpRecoveryVerifyProps } from './OtpRecoveryVerify.props';

const OtpRecoveryVerify = ({
  errorMessage,
  formState,
  isRequestingRecoveryCode = false,
  isResendCoolingDown,
  isSubmitting = false,
  remainingCooldownSeconds,
  showHeader = false,
  onBack,
  onInputBlur,
  onInputChange,
  onResendCode,
  onSubmit,
}: OtpRecoveryVerifyProps) => {
  const [titleLead, ...titleTailParts] = OTP_RECOVERY_VERIFY_TITLE.split(' ');
  const titleTail = titleTailParts.join(' ');
  const alertMessage = getFirstFieldMessage(formState.dbErrors?.recoveryCode);
  const recoveryCodeInputRef = useRef<HTMLInputElement>(null);
  const isSubmitDisabled = isOtpSubmitDisabled(formState, isSubmitting);
  const focusRecoveryCodeField = useCallback(() => {
    recoveryCodeInputRef.current?.focus();
  }, []);
  const handleInputKeyDown: NonNullable<InputProps['onKeyDown']> = useCallback(
    event => {
      if (event.key !== ENTER_KEY) {
        return;
      }

      event.preventDefault();

      if (!isSubmitDisabled) {
        onSubmit();
      }
    },
    [isSubmitDisabled, onSubmit]
  );

  useAuthInitialFocus(focusRecoveryCodeField, !isSubmitting);

  return (
    <AuthPageStack>
      {showHeader ? (
        <AuthFormHeader
          subtitle={OTP_RECOVERY_VERIFY_SUBTITLE}
          title={<AuthTitleText lead={titleLead} tail={titleTail} />}
        />
      ) : null}
      <RootPaper>
        <Stack spacing={4}>
          {!showHeader ? (
            <Stack spacing={2}>
              <Title>
                <AuthTitleText lead={titleLead} tail={titleTail} />
              </Title>
              <Subtitle>{OTP_RECOVERY_VERIFY_SUBTITLE}</Subtitle>
            </Stack>
          ) : null}
          {alertMessage ? (
            <Alert severity="error" visualVariant="inlineError">
              {alertMessage}
            </Alert>
          ) : null}
          {errorMessage ? (
            <Alert severity="error" visualVariant="inlineError">
              {errorMessage}
            </Alert>
          ) : null}
          <AuthenticationRecoveryCodeInput
            disabled={isSubmitting}
            formState={formState}
            handleBlur={onInputBlur}
            handleChange={onInputChange}
            inputRef={recoveryCodeInputRef}
            onKeyDown={handleInputKeyDown}
          />
        </Stack>
        <ActionButtons>
          <Button
            data-testid={OTP_TEST_IDS.recoveryVerifyBackButton}
            disabled={isSubmitting}
            id="auth-otp-recovery-verify-back-button"
            onClick={onBack}
            variant="text"
            visualVariant="primaryGhost"
          >
            {OTP_VERIFICATION_BACK_LABEL}
          </Button>
          <Button
            data-testid={OTP_TEST_IDS.recoveryVerifySubmitButton}
            disabled={isSubmitDisabled}
            id="auth-otp-recovery-verify-submit-button"
            loading={isSubmitting}
            onClick={onSubmit}
            sizeVariant="compact"
            sx={otpActionButtonSx}
            variant="contained"
            visualVariant="positive"
          >
            {OTP_VERIFICATION_NEXT_LABEL}
          </Button>
        </ActionButtons>
      </RootPaper>
      <AuthExtraActions>
        <AuthStatusActionLink
          component="button"
          data-testid={OTP_TEST_IDS.recoveryVerifyResendButton}
          disabled={isResendCoolingDown || isSubmitting || isRequestingRecoveryCode}
          id="auth-otp-recovery-verify-resend-button"
          onClick={onResendCode}
          type="button"
        >
          <AuthResendLabel
            label={OTP_RECOVERY_RESEND_LABEL}
            remainingSeconds={isResendCoolingDown ? remainingCooldownSeconds : undefined}
          />
        </AuthStatusActionLink>
      </AuthExtraActions>
    </AuthPageStack>
  );
};

export default OtpRecoveryVerify;
